import { useState, useEffect } from 'react';
import apiClient from '../api/client';

export const useXeroApiKey = () => {
  const [xeroApiKey, setXeroApiKey] = useState('');
  const [isSet, setIsSet] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch Xero API key
  const fetchXeroApiKey = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      const response = await apiClient.get('/settings/xero-api-key', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setXeroApiKey(response.data.xero_api_key);
      setIsSet(response.data.is_set);
      setLastUpdated(response.data.last_updated);
    } catch (err) {
      console.error('Error fetching Xero API key:', err);
      setError(err.response?.data?.detail || 'Failed to fetch Xero API key');
    } finally {
      setIsLoading(false);
    }
  };

  // Update Xero API key
  const updateXeroApiKey = async (newXeroApiKey) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      await apiClient.post('/settings/xero-api-key', 
        { xero_api_key: newXeroApiKey },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Refresh the Xero API key data
      await fetchXeroApiKey();
      return true;
    } catch (err) {
      console.error('Error updating Xero API key:', err);
      setError(err.response?.data?.detail || 'Failed to update Xero API key');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Delete Xero API key
  const deleteXeroApiKey = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      await apiClient.delete('/settings/xero-api-key', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Refresh the Xero API key data
      await fetchXeroApiKey();
      return true;
    } catch (err) {
      console.error('Error deleting Xero API key:', err);
      setError(err.response?.data?.detail || 'Failed to delete Xero API key');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Copy Xero API key to clipboard
  const copyXeroApiKey = async () => {
    try {
      await navigator.clipboard.writeText(xeroApiKey);
      return true;
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      return false;
    }
  };

  // Load Xero API key on mount
  useEffect(() => {
    fetchXeroApiKey();
  }, []);

  return {
    xeroApiKey,
    isSet,
    lastUpdated,
    isLoading,
    error,
    fetchXeroApiKey,
    updateXeroApiKey,
    deleteXeroApiKey,
    copyXeroApiKey,
  };
}; 